<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2022 (original work) Open Assessment Technologies SA ;

    import Flyout from '../flyout/Flyout.svelte';
    import { generateElementId, remToPx, getActualKey, sanitize } from '@oat-sa-private/ui-core';
    import { onDestroy } from 'svelte';

    export let anchorElement;
    export let text;

    //if the anchor is multiline we need to position tooltip differently
    let isMultilineAnchor;

    $: if (anchorElement) {
        //ensure not adding same event listeners twice
        removeAnchorEventListeners(anchorElement);
        addAnchorEventListeners(anchorElement);
    }

    // https://popper.js.org/docs/v2/modifiers/flip/
    const flipOptions = {
        name: 'flip',
        options: {
            fallbackPlacements: ['top'],
            flipVariations: false
        }
    };
    //modifiers for better tooltip positioning
    const modifiers = [
        // https://popper.js.org/docs/v2/modifiers/offset/
        {
            name: 'offset',
            options: {
                offset: ({ placement }) => {
                    if (isMultilineAnchor) {
                        //for multiline we position tooltip rather at the bottom-end or top-start
                        //see `flip` modifier's fallbackPlacements
                        //as because by default tooltip positioning relates to bounding box, the tooltip needs to be re-positioned
                        //to be displayed on top/bottom of the text, otherwise it looks detached from the anchor
                        const textStartEndX = getTextStartEndX(anchorElement);
                        if (placement === 'top-start') {
                            const anchorElementRect = anchorElement.getBoundingClientRect();
                            return [textStartEndX.start - anchorElementRect.x, remToPx(0.5)];
                        } else {
                            const anchorElementRect = anchorElement.getBoundingClientRect();
                            return [textStartEndX.end - anchorElementRect.x - anchorElementRect.width, remToPx(0.5)];
                        }
                    }
                    //for the one-line anchors tooltip will be aligned to the center of anchor
                    return [0, remToPx(0.5)];
                }
            }
        },
        flipOptions
    ];

    //we need an id to link tooltip anchor and tooltip for screen readers
    let tooltipId = generateElementId('tooltip');

    let tooltipVisible = false;

    //when anchor is clicked it receives focus which introduces race condition
    let justFocused = false;

    //default placement is centered at the bottom
    let placement = 'bottom';

    /**
     * Detects the anchor is multiline by comparing height of the first symbol rect and whole element rect
     * @param {HTMLElement} el
     * @returns {boolean}
     */
    function detectLineBreak(el) {
        const textNode = el.firstChild;
        const range = document.createRange();
        range.setStart(textNode, 0);
        range.setEnd(textNode, 1);
        const firstSymbolRect = range.getBoundingClientRect();
        const wholeSpanRect = el.getBoundingClientRect();
        return firstSymbolRect.height < wholeSpanRect.height;
    }

    /**
     * Gets X positions of text start and end using ranges API
     * @param {HTMLElement} el
     * @returns {object}
     */
    function getTextStartEndX(el) {
        const textNode = el.firstChild;

        const range = document.createRange();

        range.setStart(textNode, 0);
        range.setEnd(textNode, 1);
        const firstSymbolRect = range.getBoundingClientRect();

        range.setStart(textNode, textNode.length - 1);
        range.setEnd(textNode, textNode.length);
        const lastSymbolRect = range.getBoundingClientRect();

        return { start: firstSymbolRect.x, end: lastSymbolRect.x + lastSymbolRect.width };
    }

    /**
     * Handler for mouseIn event of tooltip anchor
     */
    function handleAnchorMouseEnter() {
        showTooltip();
    }

    /**
     * Handler for mouseOut event of tooltip anchor
     */
    function handleAnchorMouseOut() {
        hideTooltip();
    }

    /**
     * Handler for tooltip anchor click event
     */
    function handleAnchorClick() {
        if (!justFocused) {
            if (tooltipVisible) {
                hideTooltip();
            } else {
                showTooltip();
            }
        }
    }

    /**
     * Handler for tooltip anchor focus event
     */
    function handleAnchorFocus() {
        justFocused = true;
        setTimeout(() => {
            justFocused = false;
        });
        showTooltip();
    }

    /**
     * Handler for tooltip anchor blur event
     */
    function handleAnchorBlur() {
        hideTooltip();
    }

    /**
     * Handler for tooltip anchor keydown event
     * @param {Event} e
     */
    function handleAnchorKeydown(e) {
        if (getActualKey(e) === 'esc') {
            hideTooltip();
        }
    }

    /**
     * Calculates positioning and sets tooltipVisible flag to true
     */
    function showTooltip() {
        isMultilineAnchor = detectLineBreak(anchorElement);
        placement = isMultilineAnchor ? 'bottom-end' : 'bottom';
        flipOptions.options.fallbackPlacements = [isMultilineAnchor ? 'top-start' : 'top'];

        tooltipVisible = true;
    }

    /**
     * Sets tooltipVisible to false
     */
    function hideTooltip() {
        tooltipVisible = false;
    }

    /**
     * Adds event listeners to show and hide tooltip
     * @param {HTMLElement} anchorEl
     */
    function addAnchorEventListeners(anchorEl) {
        anchorEl.setAttribute('aria-describedby', tooltipId);
        anchorEl.addEventListener('focus', handleAnchorFocus);
        anchorEl.addEventListener('mouseenter', handleAnchorMouseEnter);
        anchorEl.addEventListener('mouseout', handleAnchorMouseOut);
        anchorEl.addEventListener('click', handleAnchorClick);
        anchorEl.addEventListener('blur', handleAnchorBlur);
        anchorEl.addEventListener('keydown', handleAnchorKeydown);
    }

    /**
     * Removes event listeners related to tooltip
     * @param {HTMLElement} anchorEl
     */
    function removeAnchorEventListeners(anchorEl) {
        anchorEl.removeEventListener('focus', handleAnchorFocus);
        anchorEl.removeEventListener('mouseenter', handleAnchorMouseEnter);
        anchorEl.removeEventListener('mouseout', handleAnchorMouseOut);
        anchorEl.removeEventListener('click', handleAnchorClick);
        anchorEl.removeEventListener('blur', handleAnchorBlur);
        anchorEl.removeEventListener('keydown', handleAnchorKeydown);
    }

    onDestroy(() => {
        if (anchorElement) {
            removeAnchorEventListeners(anchorElement);
        }
    });
</script>

<style>
    .tooltip-content {
        max-width: 52.5rem;
        padding: 0.5rem 1rem;
        background-color: var(--color-bg-inverted);
        color: var(--color-text-inverted);
        border-radius: 0.5rem;
        font-family: var(--font-ui);
        font-size: var(--fontsize-body);
        margin: 0;
    }

    :global(.flyout.tooltip-flyout) {
        border: none;
        background: none;
        box-shadow: none;
        z-index: var(--layer-4);
    }
</style>

<Flyout isOpen={tooltipVisible} reference={anchorElement} trigger="manual" {placement} {modifiers} className="tooltip-flyout">
    <div class="tooltip-content" role="tooltip" id={tooltipId}>
        {@html sanitize(text)}
    </div>
</Flyout>
